import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {withSize} from 'react-sizeme';
import debug from 'debug';
import {formatter, gatherData, getUpdatedFilter} from '../../../../shared/utils/utils';
import {AccountNumberMask, Dropdown, Table} from '../../../../shared';
import useQueryString from '../../../../shared/utils/useQueryString';
import useQueryObject from '../../../../shared/utils/useQueryObject';
import axiosInstance from '../../../../shared/utils/axios';
import useSWR from 'swr';
const log = debug('circleblack:investor:groupInfo:accounts:table');

const AccountsTable = ({
  containerID,
  rowsPerPage,
  onRowClick,
  rowOnClickDisabled,
  height,
  maxHeight,
  size,
}) => {
  const [type] = useQueryString('type');
  const [institutionName] = useQueryString('institutionName');

  const [filters, setFilters] = useQueryObject(
    'accountTableFilters',
    {
      type: type && {label: type, value: type} || undefined,
      institutionName,
    }
  );

  const gatherAccounts = gatherData(`/containers/${containerID}/accounts`);

  const dataManager = {
    gatherData: inputValue => gatherAccounts({
      ...inputValue,
      ...getUpdatedFilter(filters),
    }),
  };

  useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [filters]);

  const {data: accountTypes} = useSWR(`containers/${containerID}/accounts/types`, url => axiosInstance.get(url)
    .then(res => res.data.map(item => item.type))
    .then(res => [... new Set(res)])
    .then(res => res.map(item => ({label: item, value: item}))));

  const {data: institutionNames} = useSWR(`/containers/${containerID}/institutions`, url => axiosInstance.get(url)
    .then(res => res.data.map(item => item.name))
    .then(res => [... new Set(res)])
    .then(res => res.map(item => ({label: item, value: item}))));

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  const tableFilters = [
    <Dropdown key={'accountTypeSelect'}
              title={'Account Type'}
              dataPoint={{name: 'type', value: filters.type}}
              onChange={updateFilters}
              isSearchable={true}
              isClearable={true}
              options={accountTypes}/>,
    <Dropdown key={'institutionSelect'}
              title={'Institution'}
              dataPoint={{name: 'institutionName', value: filters.institutionName}}
              onChange={updateFilters}
              isSearchable={true}
              isClearable={true}
              options={institutionNames}/>,
  ];

  const searches = [
    {
      title: 'Account Name',
      name: 'name',
      isFuzzy: true,
    },
    {
      title: 'Account #',
      name: 'number',
      isFuzzy: false,
    },
  ].filter(s => !!s);

  const accountNumber = account => <AccountNumberMask number={account.number}/>;

  const columns = [
    {
      title: 'Account Name',
      dataPoint: 'name',
      type: 'text',
      render: account => account.nickname || account.name,
      minWidth: 160,
      isSortable: true,
    },
    (size.width >= 260) && {
      title: 'Account #',
      dataPoint: 'number',
      type: 'text',
      render: accountNumber,
      minWidth: 100,
      isSortable: true,
    },
    (size.width >= 600) && {
      title: 'Institution',
      dataPoint: 'institution.name',
      type: 'text',
      minWidth: 120,
      isSortable: true,
    },
    (size.width >= 480) && {
      title: 'Type',
      dataPoint: 'typeName',
      type: 'text',
      minWidth: 100,
      isSortable: true,
    },
    (size.width >= 380) && {
      title: 'Value',
      dataPoint: 'balance',
      type: 'currency',
      render: account => formatter('currency')(account.balance),
      minWidth: 120,
      isSortable: true,
    },
  ].filter(c => !!c);

  log('rendering');

  return <Table dataManager={dataManager}
                columns={columns}
                defaultSort={{
                  column: 'name',
                  desc: true,
                }}
                searches={searches}
                filters={tableFilters}
                rowsPerPage={rowsPerPage}
                rowOnClick={onRowClick}
                rowOnClickDisabled={rowOnClickDisabled}
                height={height}
                maxHeight={maxHeight}/>;
};

export default withSize()(AccountsTable);

AccountsTable.propTypes = {
  /**
   * The container ID
   */
  containerID: PropTypes.number.isRequired,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  /**
   * Optional on row click action
   */
  onRowClick: PropTypes.func,
  /**
   * Optional function to disable on row click
   */
  rowOnClickDisabled: PropTypes.func,
  /**
   * Optional max height to pass to table, defaults to 200px
   */
  height: PropTypes.string,
  /**
   * Optional max height to pass to table
   */
  maxHeight: PropTypes.string,
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
};
